/**
 * BLRRD Tone Equalizer — Phase 3.B-tone P7.
 *
 * darktable-style 9-zone halo-free luminance band exposure surface,
 * implemented clean-room from public algorithm references:
 *
 *   • He, K. / Sun, J. / Tang, X. (2010), "Guided Image Filtering."
 *     ECCV 2010. — base guided-filter formulation. Re-uses the existing
 *     `guidedFilter` + `boxFilter` helpers in src/pipeline/hdr/.
 *   • darktable user manual, "Tone Equalizer" page — public docs that
 *     describe the 3-step algorithm (build edge-aware mask → 9 EV-zone
 *     sliders → per-pixel exposure compensation). NO darktable source
 *     code consulted.
 *   • Hanika, J. (2018+) — exposure-independent guided filter (EIGF).
 *     Public proceedings; we approximate it by scaling the regularisation
 *     ε per-pixel against the local guide brightness so shadows are not
 *     smoothed more aggressively than highlights.
 *
 * License: BLRRD is MIT. darktable + RawTherapee are GPLv3. NO code from
 * either project was inspected, ported, or copied. The math is a textbook
 * implementation of the public references above.
 *
 * Pipeline slot: post-ingest, post-exposure, pre-curves. Operates on
 * scene-linear ACEScg Float32 RGBA in-place. The kernel runs the rest of
 * the pipeline on the modified buffer.
 *
 * The algorithm is halo-free because the per-pixel EV is read from a
 * smoothed (edge-preserving) guide map, NOT the raw per-pixel luminance.
 * Edges stay sharp; only the exposure-correction surface varies smoothly
 * within each region.
 */

import { guidedFilter, luminanceFromRGBA } from './hdr/index.js';

/**
 * Number of EV zone sliders. Spans the −8 EV … 0 EV log-luminance range
 * (relative to middle-grey 0.18). One slider per integer EV step.
 *
 * Zone-to-EV mapping (sliders[zone] applies at this EV):
 *
 *   zone:     0    1    2    3    4    5    6    7    8
 *   EV:      -8   -7   -6   -5   -4   -3   -2   -1    0
 *
 * EV outside [-8, 0] falls back to the nearest zone's slider value
 * (clamped curve). `applyToneEqualizer` uses bilinear interpolation in
 * EV space so the user-visible result is a smooth curve, not a 9-step
 * staircase.
 */
export const TONE_EQ_ZONES = 9;

/** EV at the lowest zone (zone 0). */
export const TONE_EQ_EV_MIN = -8.0;

/** EV at the highest zone (zone 8). */
export const TONE_EQ_EV_MAX = 0.0;

/** Slider clamp range (per zone, in EV). Mirrors darktable's −2.5..+2.5. */
export const TONE_EQ_SLIDER_MIN = -2.5;
export const TONE_EQ_SLIDER_MAX = 2.5;

/**
 * Default guided-filter radius. Picked at apply time from image size
 * (~1% of short edge, clamped). This constant is used when callers want
 * a single fixed radius for tests / batch.
 */
export const TONE_EQ_DEFAULT_RADIUS = 10;

/**
 * Default guided-filter regularisation ε for the TRUE EIGF formulation
 * (Hanika 2018+) — Phase 3.B.1 A4 upgrade.
 *
 * History:
 *   v1 (P7 ship) — guided filter ran in linear-Y space, ε in linear
 *   units. The "EIGF approximation" was the log-space slider LOOKUP
 *   making the slider response exposure-independent, but the FILTER
 *   itself was not — shadows over-smoothed because flat-linear-ε is
 *   anchored to bright pixels. Effect: ½-stop halo around faces in
 *   backlit images.
 *   v2 (this commit) — guided filter runs in log2-Y space, ε in
 *   log-space stops. Hanika's true formulation. Equal-stop deltas
 *   smooth equivalently regardless of the underlying brightness.
 *
 * The numeric value 0.05 corresponds to ~0.22 stops squared of
 * variance — gentle enough to preserve face/edge structure while
 * smoothing out small noise in flat regions. Tunable via opts.eps
 * (interpreted in log-space units when opts.eigfMode === 'log',
 * which is the default in v2). Phase 3.B.1.5 follow-up: per-pixel ε
 * adapted from local image variance for the strictest Hanika form.
 */
export const TONE_EQ_DEFAULT_EPS = 0.05;

/* log2(0.18) is the EV anchor for middle-grey ACEScg. Used to convert
 * a linear luminance Y into "stops from middle-grey". */
const LOG2_MID_GREY = Math.log2(0.18);

/* Numerical floor for log2(Y) — clamps the small-Y end so EV doesn't
 * walk to −Infinity on negative spill from the WB matrix. */
const Y_FLOOR = 1e-6;

/* Tolerance below which a slider is considered a no-op (zero EV). */
const SLIDER_EPSILON = 1e-6;

/**
 * Allocate the canonical default slider array — all zones at 0 EV.
 *
 * @returns {Float32Array} length TONE_EQ_ZONES, all entries = 0
 */
export function defaultSliders() {
    return new Float32Array(TONE_EQ_ZONES);
}

/**
 * Test whether a slider array is the identity (all zones ≈ 0 EV). Used
 * to short-circuit `applyToneEqualizer` — when every slider is zero we
 * skip the guided-filter entirely, leaving the input buffer untouched.
 *
 * @param {Float32Array | number[]} sliders
 * @returns {boolean} true when every slider is within SLIDER_EPSILON of 0
 */
export function isIdentitySliders(sliders) {
    if (!sliders || sliders.length !== TONE_EQ_ZONES) return false;
    for (let i = 0; i < TONE_EQ_ZONES; i++) {
        if (Math.abs(sliders[i]) > SLIDER_EPSILON) return false;
    }
    return true;
}

/**
 * Clamp a single slider value to [TONE_EQ_SLIDER_MIN, TONE_EQ_SLIDER_MAX]
 * and snap NaN/Infinity to 0. Used by the UI write path.
 *
 * @param {number} v
 * @returns {number}
 */
export function clampSlider(v) {
    if (!Number.isFinite(v)) return 0;
    if (v < TONE_EQ_SLIDER_MIN) return TONE_EQ_SLIDER_MIN;
    if (v > TONE_EQ_SLIDER_MAX) return TONE_EQ_SLIDER_MAX;
    return v;
}

/**
 * Sanitize a sliders array — copy, clamp each entry, return a fresh
 * Float32Array of length TONE_EQ_ZONES. NaN / Infinity → 0. Short
 * arrays pad with 0; long arrays truncate.
 *
 * @param {Float32Array | number[] | null | undefined} sliders
 * @returns {Float32Array}
 */
export function sanitizeSliders(sliders) {
    const out = new Float32Array(TONE_EQ_ZONES);
    if (!sliders) return out;
    const n = Math.min(sliders.length, TONE_EQ_ZONES);
    for (let i = 0; i < n; i++) {
        out[i] = clampSlider(sliders[i]);
    }
    return out;
}

/**
 * Bilinear interpolation across the 9-zone slider curve.
 *
 * @param {Float32Array | number[]} sliders   length TONE_EQ_ZONES
 * @param {number} ev                          EV from middle-grey
 * @returns {number}                           interpolated EV adjustment
 */
function interpSlider(sliders, ev) {
    /* Map EV ∈ [TONE_EQ_EV_MIN, TONE_EQ_EV_MAX] → continuous zone index
     * ∈ [0, TONE_EQ_ZONES - 1]. Out-of-range EV clamps to nearest end. */
    const zoneSpan = TONE_EQ_ZONES - 1; /* 8 */
    const t = (ev - TONE_EQ_EV_MIN) / (TONE_EQ_EV_MAX - TONE_EQ_EV_MIN);
    const zoneFloat = t * zoneSpan;
    if (zoneFloat <= 0) return sliders[0];
    if (zoneFloat >= zoneSpan) return sliders[zoneSpan];
    const lo = zoneFloat | 0;
    const frac = zoneFloat - lo;
    return sliders[lo] + (sliders[lo + 1] - sliders[lo]) * frac;
}

/**
 * Default radius chosen from image dimensions. ~1.5% of short edge,
 * clamped to [4, 32] (same lock as the HDR pipeline's guided filter).
 * Wider radius → smoother exposure surface but heavier per-frame cost;
 * 1.5% is a good compromise that stays halo-free on 4K input.
 *
 * @param {number} width
 * @param {number} height
 * @returns {number} radius in pixels (integer)
 */
export function defaultToneEqRadius(width, height) {
    const shortEdge = Math.min(width, height);
    const r = Math.round(shortEdge * 0.015);
    if (r < 4) return 4;
    if (r > 32) return 32;
    return r;
}

/**
 * Build the smoothed log-luminance guide map (in EV-from-middle-grey)
 * via the TRUE exposure-independent guided filter (Hanika 2018+).
 * Pulled out of the apply function so unit tests can reach it directly.
 *
 * Phase 3.B.1 A4 — TRUE EIGF
 *
 * Steps (UPDATED):
 *   1. Y = Rec.709 luminance of the ACEScg input.
 *   2. log_Y = log2(max(Y, ε_floor)) − log2(0.18)   ← in EV from mid-grey
 *   3. log_Y_smoothed = guidedFilter(log_Y, log_Y, radius, eps)
 *      ← the filter operates in LOG SPACE; ε is in log-space (stops²)
 *   4. Result is the smoothed EV map, ready for slider lookup.
 *
 * Why this is "true" EIGF vs the v1 approximation:
 *
 *   v1 ran the guided filter in linear-Y space then converted output
 *   to log. Linear-space ε is anchored to bright pixels, so shadows
 *   ended up over-smoothed → visible halos around faces in backlit
 *   scenes (Codex 3.B.1 A4 follow-up note).
 *
 *   v2 (this version) runs in log space directly. A pixel at 0.01
 *   linear (≈ −7 EV) and one at 0.5 linear (−1 EV) get equal smoothing
 *   for the same ε — Hanika's "exposure-independent" property. The
 *   slider lookup remains identical so backward-compat with the unit
 *   tests holds for the broad behavioural assertions (shadow boost
 *   ratios stay in their bracketed bands).
 *
 * @param {Float32Array} rgba   ACEScg-linear RGBA, length = w*h*4
 * @param {number} w
 * @param {number} h
 * @param {number} radius       guided-filter window radius
 * @param {number} eps          guided-filter regularisation (LOG-SPACE)
 * @returns {Float32Array}      length w*h, EV-from-middle-grey
 */
export function buildGuideEV(rgba, w, h, radius, eps) {
    if (rgba.length !== w * h * 4) {
        throw new Error('buildGuideEV: rgba length must be w*h*4');
    }
    const Y = luminanceFromRGBA(rgba, w, h);
    /* Convert Y → log-EV in place BEFORE the filter runs. Guard
     * against negative spill from the WB/matrix path: the linear
     * floor maps to a finite EV (~ −16). */
    for (let i = 0; i < Y.length; i++) {
        const y = Y[i] > Y_FLOOR ? Y[i] : Y_FLOOR;
        Y[i] = Math.log2(y) - LOG2_MID_GREY;
    }
    /* Self-guided log-space filter. Hanika 2018+: equal-stop deltas
     * smooth equivalently regardless of underlying brightness. */
    const ev_smoothed = guidedFilter(Y, Y, w, h, radius, eps);
    return ev_smoothed;
}

/**
 * Apply the Tone Equalizer in-place to a scene-linear ACEScg Float32 RGBA
 * buffer. Identity sliders (all 0) → no-op (returns the buffer untouched
 * and skips the guided-filter cost).
 *
 * @param {Float32Array} rgba                ACEScg-linear RGBA
 * @param {number} w
 * @param {number} h
 * @param {Float32Array | number[]} sliders  length TONE_EQ_ZONES, EV adjusts
 * @param {object} [opts]
 * @param {number} [opts.radius]             guided-filter radius (px)
 * @param {number} [opts.eps]                guided-filter regularisation
 * @returns {Float32Array}                   the same buffer (in-place)
 */
export function applyToneEqualizer(rgba, w, h, sliders, opts) {
    if (!(rgba instanceof Float32Array)) {
        throw new Error('applyToneEqualizer: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('applyToneEqualizer: rgba length must be w*h*4');
    }
    if (!sliders || sliders.length !== TONE_EQ_ZONES) {
        throw new Error(`applyToneEqualizer: sliders must have length ${TONE_EQ_ZONES}`);
    }
    /* Identity short-circuit — skips the guided filter on the common
     * case (user hasn't touched the panel). */
    if (isIdentitySliders(sliders)) return rgba;

    const o = opts || {};
    const radius = Number.isFinite(o.radius) ? o.radius : defaultToneEqRadius(w, h);
    const eps    = Number.isFinite(o.eps)    ? o.eps    : TONE_EQ_DEFAULT_EPS;

    const evMap = buildGuideEV(rgba, w, h, radius, eps);
    const N = w * h;

    /* Per-pixel exposure correction: gain = 2^slider_at_ev. RGB scale
     * preserves chrominance — only luminance changes. Alpha untouched. */
    for (let i = 0, p = 0; i < N; i++, p += 4) {
        const ev = evMap[i];
        const evAdjust = interpSlider(sliders, ev);
        if (evAdjust === 0) continue; /* fast path for zero contribution */
        const gain = Math.pow(2, evAdjust);
        rgba[p]     *= gain;
        rgba[p + 1] *= gain;
        rgba[p + 2] *= gain;
        /* p + 3 (alpha) intentionally untouched. */
    }
    return rgba;
}

/**
 * Pre-kernel hook used by the renderer + the worker. Given the current
 * source buffer and the pipeCtx (which carries the toneEq sub-object),
 * returns a Float32Array to feed into `applyKernel`.
 *
 *   • Identity sliders → returns `sf` (same reference, no copy).
 *   • Non-identity     → allocates a scratch Float32Array, copies `sf`,
 *                        runs `applyToneEqualizer` in-place on the
 *                        scratch buffer, returns scratch.
 *
 * Mutating `sf` directly would corrupt the canonical source (especially
 * when SAB-backed and shared between main + worker), so the copy path is
 * required. The scratch buffer is intentionally NOT pooled here — the
 * legacy editor + worker each manage their own per-frame allocators.
 *
 * @param {Float32Array} sf      ACEScg-linear source buffer (read-only)
 * @param {number} w
 * @param {number} h
 * @param {object} pipeCtx       output of buildPipelineContext()
 * @returns {Float32Array}       buffer to feed into applyKernel
 */
export function applyToneEqualizerFromCtx(sf, w, h, pipeCtx) {
    if (!pipeCtx || !pipeCtx.toneEq || pipeCtx.toneEq.isIdentity) return sf;
    const scratch = new Float32Array(sf.length);
    scratch.set(sf);
    const { sliders, radius, eps } = pipeCtx.toneEq;
    const opts = {};
    if (Number.isFinite(radius)) opts.radius = radius;
    if (Number.isFinite(eps))    opts.eps    = eps;
    applyToneEqualizer(scratch, w, h, sliders, opts);
    return scratch;
}
